import React from 'react';
import styled from 'styled-components';
import { useIsMobile } from '../../hooks/useIsScreenSize';
import { IPageConfig } from '../../models/metaTypes';
import { IChildrenProps } from '../../models/utilTypes';
import Breakpoint, { MediaQuery } from '../../styles/breakpoint';
import BottomAdUnit from '../AdUnit/BottomAdUnit';
import ContentPartner, {
  ContentPartnerMode,
  IContentPartnerProps
} from '../AdUnit/ContentPartner';

export enum ColumnLayout {
  // Single column with max-width 768px.
  SingleNarrow,

  // Single column with max-width 1240px.
  SingleWide,

  // Mobile-Tablet: Single column with max-width 768px. Desktop: 375px right-rail.
  RightRailNarrow,

  // Mobile-Tablet: Single column with max-width 1240px. Desktop: 375px right-rail.
  RightRailWide
}

const X_GUTTER = '24px';
const Y_GUTTER = '8px';

interface IColumnLayoutProps extends IChildrenProps {
  columnLayout: ColumnLayout;
  isAdIntegrationEnabled?: boolean;
  contentPartnerMode?: typeof ContentPartnerMode[keyof typeof ContentPartnerMode];
  pageConfig: IPageConfig;
}

const StyledLayoutManager = styled.div<Omit<IColumnLayoutProps, 'pageConfig'>>`
  width: 100%;
  ${props => {
    switch (props.columnLayout) {
      default:
      case ColumnLayout.SingleNarrow:
        return `
          margin: ${Y_GUTTER} 0;

          ${MediaQuery.Tablet} {
            max-width: ${Breakpoint.Tablet}px;
          }
        `;
      case ColumnLayout.SingleWide:
        return `
          margin: ${Y_GUTTER} 0;

          ${MediaQuery.Desktop} {
            max-width: ${Breakpoint.Desktop}px;
          }
        `;
      case ColumnLayout.RightRailNarrow:
        return `
          display: grid;
          justify-content: center;
          gap: ${X_GUTTER};
          margin: ${Y_GUTTER} 0;

          grid-template-columns: 100%;
          ${MediaQuery.Tablet} {
            grid-template-columns: ${Breakpoint.Tablet}px;
          }
          ${MediaQuery.Desktop} {
            grid-template-columns: ${Breakpoint.Tablet}px 375px;
            margin: ${Y_GUTTER} 0;
          }
        `;
      case ColumnLayout.RightRailWide:
        return `
          display: grid;
          justify-content: center;
          gap: ${X_GUTTER};

          grid-template-columns: 100%;
          ${MediaQuery.Desktop} {
            grid-template-columns: ${Breakpoint.Tablet}px 375px;
            margin: ${Y_GUTTER} 0;
          }
        `;
    }
  }}
`;

const StyledLayoutManagerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  background-color: ${props => props.theme.colors.surface.secondary};
`;

const LayoutManager: React.FC<IColumnLayoutProps> = ({
  children,
  columnLayout,
  isAdIntegrationEnabled = true,
  pageConfig
}) => {
  const isMobile = useIsMobile();

  const contentPartnerProps =
    pageConfig?.contentPartner || ({} as IContentPartnerProps);
  if (!contentPartnerProps.mode) {
    contentPartnerProps.mode = isMobile
      ? ContentPartnerMode.layout_1x4
      : ContentPartnerMode.layout_4x2;
  }
  if (!contentPartnerProps.placement) {
    contentPartnerProps.placement = `${isMobile ? 'Mobile' : 'Desktop'} ${
      pageConfig?.pageMetaData.pageType
    }`;
  }

  return (
    <StyledLayoutManagerWrapper>
      <StyledLayoutManager columnLayout={columnLayout}>
        {children}
      </StyledLayoutManager>
      <StyledLayoutManager columnLayout={columnLayout}>
        {isAdIntegrationEnabled && (
          <div>
            <BottomAdUnit $marginBottom="16px" />
            <ContentPartner {...contentPartnerProps} $marginBottom={Y_GUTTER} />
          </div>
        )}
        <div>{/* Holding right rail space */}</div>
      </StyledLayoutManager>
    </StyledLayoutManagerWrapper>
  );
};

export default LayoutManager;
